import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, TagDefinition, ConnectionPoint, FlatMode, ErrorLevel } from '../../../models';
import { allTimeUnits, tagSourceConnectionPointTypes, Roles, tagSaved, WideError, guidEmpty, saveErrorMessage } from '../../../common';
import {
    ExtendedVariableType,
    IAuthorizationService,
    IConnectionPointService,
    ILoggerService,
    IStoreService,
    IVariableCascadingService
} from '../../../services';
import { getDefaultQualifier, getDefaultSubtype } from '../../../services/helpers/variable.helper';

class TagsTableController implements angular.IComponentController, angular.IOnDestroy {
    columns: any[];
    connectionPoints: ConnectionPoint[];
    errorMessage: string;
    flatMode: any[];
    formController: angular.IFormController;
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    mayAcquire: boolean;
    selectedItem: any;
    selectedRowIndex: number;
    tableForm: any;
    tags: TagDefinition[];
    timeUnits = allTimeUnits;
    variables: ExtendedVariableType[];

    private _unusbscribe: () => void;

    private _types = tagSourceConnectionPointTypes;

    static $inject = [
        '$scope',
        '$rootScope',
        '$translate',
        'authorizationService',
        'storeService',
        'variableCascadingService',
        'connectionPointService',
        'loggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$translate: angular.translate.ITranslateService,
        private _authorizationService: IAuthorizationService,
        private _storeService: IStoreService,
        private _variableCascadingService: IVariableCascadingService,
        private _connectionPointService: IConnectionPointService,
        private _loggerService: ILoggerService
    ) {
    }

    $onInit() {
        this.init();

        this._$scope.$watchCollection(() => this.tags, () => {
            this.tags.forEach((t: any) => t.selectedVariableTypeGuid = t.variableTypeGuid);
        });

        this._$scope.$watch(() => this.selectedRowIndex, () => {
            if (this.selectedRowIndex !== undefined && this.selectedRowIndex <= this.tags.length) {
                this.selectedItem = this.tags[this.selectedRowIndex];
            }
        });

        this._$scope.$watch(() => this.selectedItem, () => {
            if (this.selectedItem === null) {
                this.selectedRowIndex = undefined;
            }
        });

        this.mayAcquire = this._authorizationService.isInRoles(Roles.acquisitionRoles);

        this._unusbscribe = this._storeService.register(store => {

        }, []);
    }

    $onDestroy(): void {
        if (this._unusbscribe) {
            this._unusbscribe();
        }
    }

    private init() {
        this.flatMode = [];
        this.isLoading = true;

        for (const mode of Object.keys(FlatMode)) {
            if (typeof FlatMode[mode] === 'number') {
                this.flatMode.push(mode);
            }
        }
        this._variableCascadingService
            .getPromise()
            .then(() => {
                this.variables = this._variableCascadingService.variables();
                return this._connectionPointService.getAll();
            }).then(dtos => {
                this.connectionPoints = dtos
                    .filter(cp => this._types.indexOf(cp.connectionPoint.type) >= 0)
                    .map(dto => dto.connectionPoint);
            })
            .finally(() => this.isLoading = false);
    }

    mode(flatMode: number): string {
        return FlatMode[flatMode];
    }

    getVariableName(guid) {
        return this._variableCascadingService.getVariableName(guid);
    }

    getConnectionPointName(guid): string {
        const connectionPoint = _.find(this.connectionPoints, c => c.guid === guid);
        return connectionPoint ? connectionPoint.name : '';
    }

    getQualifierName(variableTypeGuid, qualifierGuid) {
        return this._variableCascadingService.getVariableQualifierName(variableTypeGuid, qualifierGuid);
    }

    getUnitName(variableTypeGuid, unitGuid) {
        return this._variableCascadingService.getVariableUnitName(variableTypeGuid, unitGuid);
    }

    setDirty(tagDefinition) {
        tagDefinition.isDirty = true;
    }

    changeInterval(tagDefinition, data) {
        tagDefinition.lag.$enable = (data > 0);
        this.setDirty(tagDefinition);
    }

    qualifiers(variableTypeGuid) {
        return this._variableCascadingService.variableQualifiers(variableTypeGuid);
    }

    checkName(tagDefinitionGuid, $data) {
        let message: string;
        const list = this.tags.filter(t => t.name === $data);
        if (list && list.length > 0 && _.some(list, t => t.guid !== tagDefinitionGuid)) {
            message = `Name ${$data} is already in use.`;
            this.setInvalid(tagDefinitionGuid, message);
        }
        return message;
    }

    checkShortComment(tagDefinitionGuid, $data) {
        let message: string;
        if ($data.length > 16) {
            message = 'Short comment cannot exceed 16 characters.';
            this.setInvalid(tagDefinitionGuid, message);
        }
        return message;
    }

    checkSubType(tagDefinitionGuid, variableTypeGuid, $data) {
        const message = this._variableCascadingService.checkSubType(variableTypeGuid, $data);
        if (!!message) {
            this.setInvalid(tagDefinitionGuid, <string>message);
        }
        return message;
    }

    checkQualifier(tagDefinitionGuid, variableTypeGuid, $data) {
        const message = this._variableCascadingService.checkQualifier(variableTypeGuid, $data);
        if (!!message) {
            this.setInvalid(tagDefinitionGuid, <string>message);
        }
        return message;
    }

    checkUnit(tagDefinitionGuid, variableTypeGuid, $data, allowEmpty = false) {
        if (!allowEmpty) {
            const message = this._variableCascadingService.checkUnit(variableTypeGuid, $data);
            if (!!message) {
                this.setInvalid(tagDefinitionGuid, <string>message);
            }
            return message;
        }
    }

    save(): any {
        this.errorMessage = '';
        const self = this;
        self.isLoading = true;
        const tagsToUpdate = this.tags.filter((t: any) => t.isDirty);
        tagsToUpdate.forEach(t => {
            t.variableSubTypeGuid = t.variableSubtypeGuid;
            (<any>t).selectedVariableTypeGuid = t.variableTypeGuid;
            (<any>t).variableTypeName = this.getVariableName(t.variableTypeGuid);
            (<any>t).subtypeName = this.getVariableSubTypeName(t.variableTypeGuid, t.variableSubtypeGuid);
            (<any>t).qualifierName = this.getQualifierName(t.variableTypeGuid, t.qualifierGuid);
            (<any>t).unitName = this.getUnitName(t.variableTypeGuid, t.unitGuid);
        });
        // this._tagDefinitionService.update(tagsToUpdate)
        return this._storeService.dispatchers()
            .tags
            .update(tagsToUpdate)
            .then(data => {
                self.tableForm.$setPristine();
                self.clean();
                self._loggerService.logSuccess(tagsToUpdate.length + ' tags updated.');
                this._$rootScope.$broadcast(tagSaved, tagsToUpdate);
            })
            .catch((reason) => {
                self._loggerService.logError('Failed to save tags.');
                if (reason.modelState) {
                    self.errorMessage = 'Some tags are not properly configured and cannot be saved.';
                }
            })
            .finally(() => this.isLoading = false);
        /* TODO:
         * -post to server
         * -set dirty flags to false if success
         */
    }

    cancel() {
        this.clean();
        this.tableForm.$cancel();
        this.tableForm.$setPristine();
    }

    click($index: number) {
        this.selectedItem = this.tags[$index];
    }

    setClickedRow(index: number, $event: angular.IAngularEvent): void {
        if (!this.formController || !this.formController.$dirty) {
            this.selectedRowIndex = index;
            //prevent event default to keep selection in case of delete. If event propagates, tag gets deselected in delete.
            $event.preventDefault();
        } else {
            const msg = this._$translate.instant(saveErrorMessage);
            this._loggerService.logWarning(msg);
            $event.stopPropagation();
        }
    }

    isVisible(columnName: string) {
        const found: any = _.find(this.columns, c => c.label === columnName);
        return !found || found.isVisible;
    }

    getClass(tag, $index: number) {
        const result: any = {};
        if (!!tag.errors && tag.errors.length > 0) {
            if (_.some(tag.errors, (e: WideError) => e.level === ErrorLevel.Error)) {
                result['alert alert-danger'] = true;
            } else if (_.some(tag.errors, (e: WideError) => e.level === ErrorLevel.Warning)) {
                result['alert alert-warning'] = true;
            }
        }
        result['alert alert-danger'] = result['alert alert-danger'] || (tag.isDirty && tag.isInvalid);
        result['row-highlight'] = this.selectedRowIndex === $index; // && !this.tableForm.$visible;
        result['alert alert-info'] = tag.isDirty && !tag.isInvalid;
        return result;
    }

    timeUnit(value: number): string {
        const keyValuePair = _.find(this.timeUnits, u => u.key === value);
        return keyValuePair ? keyValuePair.value : '';
    }

    getError(tag) {
        let result: WideError;
        if (!!tag.errors) {
            const error = _.find(tag.errors, (e: WideError) => e.level === ErrorLevel.Error);
            if (error) {
                result = error;
            } else {
                result = _.find(tag.errors, (e: WideError) => e.level === ErrorLevel.Warning);
            }
        }
        return result;
    }

    private setInvalid(guid, errorMessage?: string) {
        const td: any = _.find(this.tags, t => t.guid === guid);
        if (errorMessage) {
            td.errorMessage = errorMessage;
        }
        td.isInvalid = true;
    }

    private clean() {
        this.tags.forEach((t: any) => {
            t.isDirty = false;
            t.selectedVariableTypeGuid = t.variableTypeGuid;
            t.isInvalid = false;
        });
    }

    changeSubTypes(tagDefinition, variableTypeGuid: string) {
        tagDefinition.selectedVariableTypeGuid = variableTypeGuid;
        const vt = _.find(this.variables, v => v.guid === variableTypeGuid);
        if (vt) {
            const defaultSubType = getDefaultSubtype(tagDefinition.variableSubtypeGuid, vt.subtypes.map(d => { return { key: d.guid, value: d.name }; }));
            if (defaultSubType && defaultSubType !== tagDefinition.variableSubtypeGuid) {
                tagDefinition.variableSubtypeGuid = defaultSubType;
            }
            const defaultQualifier = getDefaultQualifier(tagDefinition.qualifierGuid, vt.qualifiers.map(d => { return { key: d.guid, value: d.name }; }));
            if (defaultQualifier && defaultQualifier !== tagDefinition.qualifierGuid) {
                tagDefinition.qualifierGuid = defaultQualifier;
            }
            tagDefinition.unitGuid = vt.internalUnitGuid;
        }
        this.setDirty(tagDefinition);
    }

    variableSubTypes(variableTypeGuid) {
        return this._variableCascadingService.variableSubTypes(variableTypeGuid);
    }

    variableUnits(variableTypeGuid: string, allowEmpty = false) {
        const result = this._variableCascadingService.variableUnits(variableTypeGuid);
        if (allowEmpty && result[0].name !== '') {
            result.unshift({ guid: guidEmpty, name: '' });
        }
        return result;
    }

    getVariableSubTypeName(variableTypeGuid, variableSubtypeGuid) {
        return this._variableCascadingService.getVariableSubTypeName(variableTypeGuid, variableSubtypeGuid);
    }
}

export const wtTagsTable: ComponentDefinition = {
    componentId: 'wtTagsTable',
    controller: TagsTableController,
    template: require('./tagsTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        tags: '=',
        tableForm: '=',
        selectedItem: '=',
        formController: '=',
        columns: '=',
        isEditMode: '='
    }
};
